import React, { useMemo } from 'react';
import { Table, TableColumnType, Tag, Button, Image, ColorPicker, Space } from 'antd';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { BenefitRecord, BenefitType } from '../types';
import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import {
  getTypeLimitText,
  getStatusInfo,
} from '../utils';
import {
  parseGradientString,
  getDefaultGradient,
  convertColorPairToGradient,
  isColorPairFormat,
} from '../utils/gradientUtils';
import { getBenefitTypeList } from '../api';
import { QUERY_KEYS } from '../constants';

interface BenefitTableProps {
  data: BenefitRecord[];
  loading: boolean;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize?: number) => void;
  };
  onEdit: (record: BenefitRecord) => void;
}

const ICON_SIZE = 60;

const BenefitTable: React.FC<BenefitTableProps> = ({
  data,
  loading,
  pagination,
  onEdit,
}) => {
  const isMobile = useAtomValue(isMobileAtom);

  // 获取权益类型列表
  const { data: benefitTypeList = [] } = useQuery({
    queryKey: [QUERY_KEYS.BENEFIT_TYPE_LIST],
    queryFn: getBenefitTypeList,
  });

  // 创建 id 到 name 的映射
  const benefitTypeMap = useMemo(() => {
    const map: Record<number, string> = {};
    benefitTypeList.forEach((item) => {
      map[item.id] = item.name;
    });
    return map;
  }, [benefitTypeList]);

  const columns = useMemo<TableColumnType<BenefitRecord>[]>(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80,
      },
      {
        title: '会员权益图标',
        dataIndex: 'iconUrl',
        key: 'iconUrl',
        width: 140,
        render: (iconUrl: string) =>
          iconUrl ? (
            <Image
              src={iconUrl}
              alt="权益图标"
              width={ICON_SIZE}
              height={ICON_SIZE}
              style={{ objectFit: 'cover' }}
              preview={{ mask: '预览' }}
            />
          ) : (
            '-'
          ),
      },
      {
        title: '权益名称（英文）',
        dataIndex: 'titleEn',
        key: 'titleEn',
        width: 180,
      },
      {
        title: '权益名称（日文）',
        dataIndex: 'titleJa',
        key: 'titleJa',
        width: 190,
      },
      {
        title: '类型',
        dataIndex: 'type',
        key: 'type',
        width: 120,
        render: (type: number) => {
          const text = benefitTypeMap[type] || '未知';
          return <Tag>{text}</Tag>;
        },
      },
      {
        title: '类型限制',
        dataIndex: 'typeLimit',
        key: 'typeLimit',
        width: 180,
        render: (typeLimit: string, record: BenefitRecord) => {
          // 如果是昵称效果类型，显示 ColorPicker
          if (record.type === BenefitType.NICKNAME) {
            let pickerValue: { color: string; percent: number }[];
            if (!typeLimit || typeLimit === '') {
              pickerValue = getDefaultGradient();
            } else if (typeof typeLimit === 'string' && typeLimit.includes('linear-gradient')) {
              // 已经是 linear-gradient 格式，直接解析
              const parsed = parseGradientString(typeLimit);
              pickerValue = parsed || getDefaultGradient();
            } else if (typeof typeLimit === 'string' && isColorPairFormat(typeLimit)) {
              // 如果是 #color1,#color2 格式，转换为 linear-gradient 格式后再解析
              const gradientString = convertColorPairToGradient(typeLimit);
              if (gradientString) {
                const parsed = parseGradientString(gradientString);
                pickerValue = parsed || getDefaultGradient();
              } else {
                pickerValue = getDefaultGradient();
              }
            } else {
              pickerValue = getDefaultGradient();
            }

            return (
              <Space>
                <span>昵称效果:</span>
                <ColorPicker
                  value={pickerValue}
                  disabled
                  mode="gradient"
                  size="small"
                />
              </Space>
            );
          }

          // 其他类型显示文本
          const text = getTypeLimitText(typeLimit, record.type);
          return <span>{text}</span>;
        },
      },
      {
        title: '核心权益',
        dataIndex: 'corePrivilege',
        key: 'corePrivilege',
        width: 90,
        render: (val: number) => val === 1 ? <Tag color="green">是</Tag> : <Tag>否</Tag>,
      },
      {
        title: '分组标识',
        dataIndex: 'groupKey',
        key: 'groupKey',
        width: 130,
        render: (val: string) => val || '-',
      },
      {
        title: '权益大类',
        dataIndex: 'groupCategory',
        key: 'groupCategory',
        width: 120,
        render: (val: string) => {
          const map: Record<string, string> = {
            coins: '金币権益',
            create: '創作権益',
            trade: '取引権益',
            exclusive: '専属権益',
          };
          return val ? <Tag>{map[val] || val}</Tag> : '-';
        },
      },
      {
        title: '排序值',
        dataIndex: 'showOrder',
        key: 'showOrder',
        width: 100,
        render: (showOrder: number) => showOrder ?? '-',
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (status: number) => {
          const { text, color } = getStatusInfo(status);
          return <Tag color={color}>{text}</Tag>;
        },
      },
      {
        title: '创建时间',
        dataIndex: 'createTime',
        key: 'createTime',
        width: 180,
        render: (time: string) =>
          time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
      },
      {
        title: '更新时间',
        dataIndex: 'updateTime',
        key: 'updateTime',
        width: 180,
        render: (time: string) =>
          time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
      },
      {
        title: '更新人',
        dataIndex: 'updateUserName',
        key: 'updateUserName',
        width: 120,
        render: (name: string) => name || '-',
      },
      {
        title: '操作',
        key: 'action',
        width: 100,
        fixed: isMobile ? 'right' : undefined,
        render: (_, record) => (
          <Button type="link" onClick={() => onEdit(record)}>
            编辑
          </Button>
        ),
      },
    ],
    [isMobile, onEdit, benefitTypeMap]
  );

  return (
    <Table<BenefitRecord>
      columns={columns}
      dataSource={data}
      rowKey="id"
      loading={loading}
      scroll={{ x: isMobile ? 1200 : undefined }}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        onChange: pagination.onChange,
        onShowSizeChange: pagination.onChange,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total) => `共 ${total} 条`,
        pageSizeOptions: ['10', '20', '50', '100'],
      }}
      size={isMobile ? 'small' : 'middle'}
    />
  );
};

export default BenefitTable;

